import { CatalogSchema } from '@contracts/schemas/catalog.schema';
import { pageOf } from '@contracts/schemas/common.schema';
import {
  CreateJobOrderResultSchema,
  JobOrderCountsSchema,
  JobOrderSchema,
  JobOrderWithKitsSchema,
  UpdateJobOrderResultSchema,
} from '@contracts/schemas/jobOrder.schema';
import { PrintPayloadSchema } from '@contracts/schemas/print.schema';
import { BulkResultSchema } from '@contracts/schemas/bulk.schema';
import { ReceiptSchema } from '@contracts/schemas/receipt.schema';
import {
  AccountsReviewDetailSchema,
  ReviewCleanupResultSchema,
  ReviewCleanupSchema,
} from '@contracts/schemas/accountsReview.schema';
import type {
  BulkResult,
  CatalogItemView,
  CreateJobOrderCommand,
  CreateJobOrderResult,
  DeletionCommand,
  DispatchCommand,
  JobOrderCounts,
  MarkDeliveredCommand,
  SetDatesCommand,
  UpdateJobOrderCommand,
  UpdateJobOrderResult,
  UpdateShippingCommand,
  JobOrderFilter,
  JobOrderPage,
  JobOrderRepository,
  Receipt,
  SaveReceiptCommand,
} from '@/application/ports/JobOrderRepository';
import type { PrintPayload, PrintRepository } from '@/application/ports/PrintRepository';
import type { AccountsReviewDetail, JobOrder, Kit, ReviewCleanup } from '@/domain';
import { request } from '../client';

const JobOrderPageSchema = pageOf(JobOrderSchema);

function query(filter: JobOrderFilter): string {
  const params = new URLSearchParams();
  if (filter.state !== undefined) params.set('state', filter.state);
  if (filter.urgent !== undefined) params.set('urgent', String(filter.urgent));
  if (filter.origin !== undefined) params.set('origin', filter.origin);
  if (filter.dentist !== undefined) params.set('dentist', filter.dentist);
  if (filter.includeDispatched === true) params.set('includeDispatched', 'true');
  if (filter.due !== undefined) params.set('due', filter.due);
  if (filter.deliveredSince !== undefined) params.set('deliveredSince', filter.deliveredSince);
  if (filter.money !== undefined) params.set('money', filter.money);
  if (filter.age !== undefined) params.set('age', filter.age);
  if (filter.active === true) params.set('active', 'true');
  if (filter.status !== undefined) params.set('status', filter.status);
  if (filter.page !== undefined) params.set('page', String(filter.page));
  if (filter.pageSize !== undefined) params.set('pageSize', String(filter.pageSize));
  if (filter.q !== undefined && filter.q !== '') params.set('q', filter.q);
  if (filter.sort !== undefined) params.set('sort', filter.sort);
  const search = params.toString();
  return search === '' ? '' : `?${search}`;
}

export class HttpJobOrderRepository implements JobOrderRepository, PrintRepository {
  async list(filter: JobOrderFilter): Promise<JobOrderPage> {
    const result = await request(`/job-orders${query(filter)}`, { schema: JobOrderPageSchema });
    return result as unknown as JobOrderPage;
  }

  async byNo(jobOrderNo: string): Promise<{ jobOrder: JobOrder; kits: Kit[] }> {
    const result = await request(`/job-orders/${jobOrderNo}`, { schema: JobOrderWithKitsSchema });
    return result as unknown as { jobOrder: JobOrder; kits: Kit[] };
  }

  counts(): Promise<JobOrderCounts> {
    return request('/job-orders/counts', { schema: JobOrderCountsSchema });
  }

  async release(jobOrderNo: string): Promise<JobOrder> {
    const result = await request(`/job-orders/${jobOrderNo}/release`, {
      method: 'POST',
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async takeBackRelease(jobOrderNo: string): Promise<JobOrder> {
    const result = await request(`/job-orders/${jobOrderNo}/release`, {
      method: 'DELETE',
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async markPaid(jobOrderNo: string): Promise<JobOrder> {
    const result = await request(`/job-orders/${jobOrderNo}/payment`, {
      method: 'POST',
      body: { paid: true },
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async undoPayment(jobOrderNo: string): Promise<JobOrder> {
    const result = await request(`/job-orders/${jobOrderNo}/payment`, {
      method: 'DELETE',
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  receipt(jobOrderNo: string): Promise<Receipt> {
    return request(`/job-orders/${jobOrderNo}/receipt`, { schema: ReceiptSchema });
  }

  saveReceipt(command: SaveReceiptCommand): Promise<Receipt> {
    return request(`/job-orders/${command.jobOrderNo}/receipt`, {
      method: 'PUT',
      body: { lines: command.lines },
      schema: ReceiptSchema,
    });
  }

  accountsReviewDetail(jobOrderNo: string): Promise<AccountsReviewDetail> {
    return request(`/job-orders/${jobOrderNo}/accounts-review`, {
      schema: AccountsReviewDetailSchema,
    });
  }

  reviewCleanup(): Promise<ReviewCleanup> {
    return request('/accounts/review-cleanup', { schema: ReviewCleanupSchema });
  }

  async clearReviewCleanup(): Promise<number> {
    const result = await request('/accounts/review-cleanup', {
      method: 'POST',
      schema: ReviewCleanupResultSchema,
    });
    return result.cleared;
  }

  async accountsReviewed(jobOrderNo: string, seen?: string): Promise<JobOrder> {
    const result = await request(`/job-orders/${jobOrderNo}/accounts-review`, {
      method: 'POST',
      // Only a screen that showed the change says which one it read; with no
      // body the server answers as it always did.
      ...(seen === undefined ? {} : { body: { seen } }),
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async accountsUnreview(jobOrderNo: string): Promise<JobOrder> {
    const result = await request(`/job-orders/${jobOrderNo}/accounts-review`, {
      method: 'DELETE',
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async setDates(command: SetDatesCommand): Promise<JobOrder> {
    const result = await request(`/job-orders/${command.jobOrderNo}/dates`, {
      method: 'PATCH',
      body: { startDate: command.startDate, deliveryDate: command.deliveryDate },
      rowVersion: command.rowVersion,
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async updateJobOrder(command: UpdateJobOrderCommand): Promise<UpdateJobOrderResult> {
    const result = await request(`/job-orders/${command.jobOrderNo}`, {
      method: 'PATCH',
      body: command.changes,
      rowVersion: command.rowVersion,
      schema: UpdateJobOrderResultSchema,
    });
    return result as unknown as UpdateJobOrderResult;
  }

  async hold(jobOrderNo: string, reason: string): Promise<JobOrder> {
    const result = await request(`/job-orders/${jobOrderNo}/hold`, {
      method: 'POST',
      body: { reason },
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async releaseHold(jobOrderNo: string): Promise<JobOrder> {
    const result = await request(`/job-orders/${jobOrderNo}/hold`, {
      method: 'DELETE',
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async dispatch(command: DispatchCommand): Promise<JobOrder> {
    const result = await request(`/job-orders/${command.jobOrderNo}/dispatch`, {
      method: 'POST',
      // Only what was given travels: an absent code is a code that comes later.
      body: {
        method: command.method,
        ...(command.carrier === undefined ? {} : { carrier: command.carrier }),
        ...(command.carrierOther === undefined || command.carrierOther === ''
          ? {}
          : { carrierOther: command.carrierOther }),
        ...(command.trackingCode === undefined || command.trackingCode === ''
          ? {}
          : { trackingCode: command.trackingCode }),
        ...(command.driverName === undefined || command.driverName === ''
          ? {}
          : { driverName: command.driverName }),
        ...(command.note === undefined || command.note === '' ? {} : { note: command.note }),
        ...(command.dispatchedAt === undefined ? {} : { dispatchedAt: command.dispatchedAt }),
      },
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async updateShipping(command: UpdateShippingCommand): Promise<JobOrder> {
    const result = await request(`/job-orders/${command.jobOrderNo}/shipping`, {
      method: 'PATCH',
      body: command.changes,
      rowVersion: command.rowVersion,
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async markDelivered(command: MarkDeliveredCommand): Promise<JobOrder> {
    const result = await request(`/job-orders/${command.jobOrderNo}/shipping/delivered`, {
      method: 'POST',
      body: {
        ...(command.deliveredAt === undefined ? {} : { deliveredAt: command.deliveredAt }),
        ...(command.receivedBy === undefined || command.receivedBy === ''
          ? {}
          : { receivedBy: command.receivedBy }),
      },
      rowVersion: command.rowVersion,
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async requestDeletion(command: DeletionCommand & { reason: string }): Promise<JobOrder> {
    const result = await request(`/job-orders/${command.jobOrderNo}/deletion`, {
      method: 'POST',
      body: { reason: command.reason },
      rowVersion: command.rowVersion,
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async withdrawDeletion(command: DeletionCommand): Promise<JobOrder> {
    const result = await request(`/job-orders/${command.jobOrderNo}/deletion`, {
      method: 'DELETE',
      rowVersion: command.rowVersion,
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async approveDeletion(command: DeletionCommand): Promise<JobOrder> {
    const result = await request(`/job-orders/${command.jobOrderNo}/deletion/approve`, {
      method: 'POST',
      rowVersion: command.rowVersion,
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  async restoreCase(command: DeletionCommand): Promise<JobOrder> {
    const result = await request(`/job-orders/${command.jobOrderNo}/restore`, {
      method: 'POST',
      rowVersion: command.rowVersion,
      schema: JobOrderSchema,
    });
    return result as unknown as JobOrder;
  }

  /** A 204: there is no case left to answer with. */
  async purgeCase(command: DeletionCommand): Promise<void> {
    await request(`/job-orders/${command.jobOrderNo}/purge`, {
      method: 'POST',
      rowVersion: command.rowVersion,
    });
  }

  /*
   * A selection of cases. Section 4 of the request of 24 September 2026. Not
   * queued offline: these are a manager's decisions about many cases at once,
   * and replaying one an hour later against a floor that has moved on would
   * archive or delete cases nobody chose in that state.
   */
  async archiveMany(jobOrderNos: string[], reason?: string): Promise<BulkResult> {
    return request('/job-orders/bulk/archive', {
      method: 'POST',
      body: reason === undefined || reason.trim() === '' ? { jobOrderNos } : { jobOrderNos, reason },
      schema: BulkResultSchema,
    });
  }

  async unarchiveMany(jobOrderNos: string[]): Promise<BulkResult> {
    return request('/job-orders/bulk/unarchive', {
      method: 'POST',
      body: { jobOrderNos },
      schema: BulkResultSchema,
    });
  }

  async deleteMany(jobOrderNos: string[], reason: string): Promise<BulkResult> {
    return request('/job-orders/bulk/deletion', {
      method: 'POST',
      body: { jobOrderNos, reason },
      schema: BulkResultSchema,
    });
  }

  /* The money actions on many cases, and their Undo. The lab's request of 17 October 2026, items 9 and 10. */
  payMany(jobOrderNos: string[]): Promise<BulkResult> {
    return this.money('pay', jobOrderNos);
  }

  unpayMany(jobOrderNos: string[]): Promise<BulkResult> {
    return this.money('unpay', jobOrderNos);
  }

  releaseMany(jobOrderNos: string[]): Promise<BulkResult> {
    return this.money('release', jobOrderNos);
  }

  unreleaseMany(jobOrderNos: string[]): Promise<BulkResult> {
    return this.money('unrelease', jobOrderNos);
  }

  private money(action: 'pay' | 'unpay' | 'release' | 'unrelease', jobOrderNos: string[]): Promise<BulkResult> {
    return request(`/job-orders/bulk/${action}`, {
      method: 'POST',
      body: { jobOrderNos },
      schema: BulkResultSchema,
    });
  }

  async create(command: CreateJobOrderCommand, requestId?: string): Promise<CreateJobOrderResult> {
    const result = await request('/job-orders', {
      method: 'POST',
      body: command,
      schema: CreateJobOrderResultSchema,
      idempotencyKey: requestId,
    });

    return result as unknown as CreateJobOrderResult;
  }

  async catalog(): Promise<{ items: CatalogItemView[]; categories: string[] }> {
    const result = await request('/catalog', { schema: CatalogSchema });
    return { items: result.items, categories: result.categories };
  }

  /** Section 7.7. Everything the A4 sheet needs, so printing works from a cold cache. */
  async printPayload(kitId: string): Promise<PrintPayload> {
    const result = await request(`/kits/${kitId}/print`, { schema: PrintPayloadSchema });
    return result as unknown as PrintPayload;
  }
}
